import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmlakKamulastirmaPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Erişim kontrolü
  if (!user?.is_admin && !user?.has_emlak_kamulastirma_access) {
    navigate('/dashboard');
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" 
                className="w-12 h-12 object-contain cursor-pointer" 
                onClick={() => navigate('/dashboard')} 
              />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Emlak ve Kamulaştırma</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">
                Anasayfa
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-300 hover:text-white">
                Dashboard
              </Button>
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">🏢 Emlak ve Kamulaştırma Modülü</h2>
          <p className="text-gray-400">Emlak ve kamulaştırma işlemleri takip sistemi</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Kamulaştırma İşlemleri */}
          <Card className="bg-gray-900 border-gray-800 hover:border-amber-700/50 transition-colors">
            <CardHeader>
              <CardTitle className="text-white">🏛️ Kamulaştırma İşlemleri</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 mb-4">İl Müdürlüğü tarafından kamulaştırılan taşınmazların değer tespit, komisyon, uzlaşma bedellerini takip edin.</p>
              <Button
                className="w-full bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate('/emlak-kamulastirma/kamulastirma-islemleri')}
                data-testid="kamulastirma-islemleri-btn"
              >
                Kamulaştırma İşlemlerine Git
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800 hover:border-blue-700/50 transition-colors">
            <CardHeader>
              <CardTitle className="text-white">🏠 Emlak İşlemleri</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 mb-4">Bina ve arsa kayıtlarını buradan yönetebilirsiniz.</p>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/emlak-kamulastirma/emlak-islemleri')}
              >
                Emlak İşlemlerine Git
              </Button>
            </CardContent>
          </Card>

          {/* Kiralık Taşınmaz İşlemleri */}
          <Card className="bg-gray-900 border-gray-800 hover:border-purple-700/50 transition-colors">
            <CardHeader>
              <CardTitle className="text-white">🔑 Kiralık Taşınmaz İşlemleri</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 mb-4">Kurumumuzun kiraladığı taşınmazların başlangıç/bitiş tarihleri ve kira bedellerini (KDV dahil) yönetin.</p>
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={() => navigate('/emlak-kamulastirma/kiralik-tasinmaz')}
                data-testid="kiralik-tasinmaz-btn"
              >
                Kiralık Taşınmazlara Git
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800 hover:border-emerald-700/50 transition-colors">
            <CardHeader>
              <CardTitle className="text-white">🗺 İstanbul Harita Görünümü</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 mb-4">Tesis envanterini İstanbul haritası üzerinde görüntüleyin.</p>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/emlak-kamulastirma/harita')}
                data-testid="harita-gorunumu-btn"
              >
                Haritayı Aç
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Info Box */}
        <Card className="bg-blue-900/20 border-blue-800 mt-8">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-blue-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-white font-semibold mb-2">İpucu</h3>
                <p className="text-blue-200 text-sm">
                  <strong>Kamulaştırma İşlemleri</strong> sayfası, Emlak İşlemleri'nde mülkiyet "İL MÜDÜRLÜĞÜ (KAMULAŞTIRMA)" olarak işaretlenen kayıtları otomatik listeler.
                  <strong> Kiralık Taşınmaz</strong> sayfası ise "Tahsis/Kiraya Verildi mi/Kiralandı Mı?" alanı "Kiralandı" olan kayıtları gösterir.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EmlakKamulastirmaPage;
